import * as React from 'react'
import type { PasswordStrength } from 'types/business'
import i18next from '@disco/i18n/i18next'
import {
  Button,
  Flex,
  PasswordTextField,
  Select,
  Spinner,
  Text,
} from 'components/ds'
import InlineError from 'components/ui/inline-error'
import styles from './welcome-form.module.css'
type Props = {
  password: string
  passwordConfirmation: string
  passwordChangeInProgress?: boolean
  onSubmit: () => unknown
  onPasswordChange: any
  onPasswordConfirmationChange: any
  passwordStrength: PasswordStrength
  errors: Record<string, React.ReactNode | null | undefined>
}

/**
 * Password change form for welcome/account creation flow
 * Includes password confirmation, terms acceptance, and account creation button
 * @param password - New password value
 * @param passwordConfirmation - Password confirmation value
 * @param passwordChangeInProgress - Whether password change is in progress
 * @param onSubmit - Callback when form is submitted
 * @param onPasswordChange - Callback when password input changes
 * @param onPasswordConfirmationChange - Callback when confirmation input changes
 * @param passwordStrength - Business password strength requirements
 * @param errors - Error messages for form validation
 */
const ChangePasswordWelcomeForm = (props: Props): React.ReactElement<'div'> => {
  const [language, setLanguage] = React.useState(i18next.language || 'en')

  const handleLanguageChange = (value: string) => {
    setLanguage(value)
    i18next.changeLanguage(value)
  }

  const submitEnabled =
    props.password &&
    props.passwordConfirmation &&
    !props.errors['re-enter-new-password'] &&
    !props.errors['new-password']
  return (
    <div className="ChangePasswordWelcomeForm">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="2">
            <Text as="label" size="3" weight="medium">
              Choose a new password
            </Text>
            <PasswordTextField
              size="3"
              autoComplete="new-password"
              onChange={props.onPasswordChange}
              value={props.password}
              strength={props.passwordStrength}
            />
            <InlineError
              error={
                props.errors['new-password'] ?? props.errors['weak-password']
              }
            />
          </Flex>
          <Flex direction="column" gap="2">
            <Text as="label" size="3" weight="medium">
              Re-enter password
            </Text>
            <PasswordTextField
              size="3"
              autoComplete="new-password"
              onChange={props.onPasswordConfirmationChange}
              value={props.passwordConfirmation}
            />
            <InlineError error={props.errors['re-enter-new-password']} />
          </Flex>
        </Flex>
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="2" align="start">
            <Text as="label" size="3" weight="medium">
              Account language
            </Text>
            <Select.Root
              size="3"
              value={language}
              onValueChange={handleLanguageChange}
            >
              <Select.Trigger
                variant="soft"
                className={styles.languageSelect}
              />
              <Select.Content>
                <Select.Item value="en">English</Select.Item>
                <Select.Item value="ja">Japanese</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>
          <Text as="p" size="1" className={styles.termsContainer}>
            By clicking <b>Create account</b> you agree to be bound by our{' '}
            <a
              className={styles.termsLink}
              href="https://disco.ac/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              className={styles.termsLink}
              href="https://disco.ac/security"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </Text>
          <InlineError error={props.errors['current-password']} />
          <Button
            size="3"
            mt="2"
            disabled={!submitEnabled || props.passwordChangeInProgress}
            onClick={props.onSubmit}
            className={styles.submitButton}
          >
            {props.passwordChangeInProgress ? (
              <Flex align="center" gap="2">
                <Spinner size="1" />
                Creating account&hellip;
              </Flex>
            ) : (
              'Create account'
            )}
          </Button>
        </Flex>
      </Flex>
    </div>
  )
}

export default ChangePasswordWelcomeForm
